import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import { getDecodeId } from "slices/userProfile/thunk";
import { HomeownerSelection } from "./HomeownerSelection";
import { ConfirmationScreen } from "./ConfirmationScreen";
import { SuccessScreen } from "./SuccessScreen";
import { ContractorExplorer } from "./ContractorExplorer";
import { Contractor, Homeowner } from "./data";
import { Container } from "react-bootstrap";
import { RecommendationHistory } from "./RecommendationHistory";
import "./explore-contractors.css";
type Step = "contractors" | "homeowners" | "confirmation" | "success";

const CONTRACTORS_PATH = "/api/crm/contractors";
const CREATE_SUGGESTION_PATH = "/agent/suggest-contractors";
const ACCESSIBLE_HOMEOWNERS_PATH = "/agent/accessible-homeowners";

function parseAvailability(rawAvailability: any) {
  if (!rawAvailability) return "";

  if (typeof rawAvailability === "string") {
    try {
      const parsed = JSON.parse(rawAvailability);

      if (Array.isArray(parsed)) {
        return parsed
          .map((item: any) =>
            typeof item === "string" ? item : item?.label || item?.value || ""
          )
          .filter(Boolean)
          .join(", ");
      }

      if (typeof parsed === "object" && parsed !== null) {
        return parsed.label || parsed.value || "";
      }

      return typeof parsed === "string" ? parsed : "";
    } catch {
      return rawAvailability.trim();
    }
  }

  if (Array.isArray(rawAvailability)) {
    return rawAvailability
      .map((item: any) =>
        typeof item === "string" ? item : item?.label || item?.value || ""
      )
      .filter(Boolean)
      .join(", ");
  }

  if (typeof rawAvailability === "object") {
    return rawAvailability.label || rawAvailability.value || "";
  }

  return "";
}

export default function ContractExplorePage() {
  const [pageView, setPageView] = useState<"recommend" | "history">("recommend");
  const [currentStep, setCurrentStep] = useState<Step>("contractors");
  const [selectedContractorIds, setSelectedContractorIds] = useState<string[]>([]);
  const [selectedHomeownerIds, setSelectedHomeownerIds] = useState<string[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [homeowners, setHomeowners] = useState<Homeowner[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [loadingHomeowners, setLoadingHomeowners] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const agentId = String(getDecodeId() || "");

  useEffect(() => {
    setLoadingContractors(true);
    CallAPIGetPromise(CONTRACTORS_PATH)
      .then((res: any) => {
        const contractorRows = res?.data?.data || [];
        const mappedContractors = contractorRows.map((contractor: any) => {
          // 🔹 Parse Profile
          let parsedProfile = "";
          try {
            if (contractor.ua_profile) {
              const profileObj = JSON.parse(contractor.ua_profile);
              parsedProfile = profileObj?.label || "";
            }
          } catch (e) {
            parsedProfile = "";
          }
        
         
          // 🔹 Parse Skills
          let parsedSkills: string[] = [];
          if (Array.isArray(contractor.ua_skills)) {
            parsedSkills = contractor.ua_skills;
          } else if (typeof contractor.ua_skills === "string") {
            try {
              const arr = JSON.parse(contractor.ua_skills);
              parsedSkills = Array.isArray(arr) ? arr : [];
            } catch {
              parsedSkills = contractor.ua_skills
                .replace(/^\[|\]$/g, "")
                .replace(/"/g, "")
                .split(",")
                .map((item: string) => item.trim())
                .filter(Boolean);
            }
          }
        
          const parsedAvailability = parseAvailability(contractor.ua_work_availbility);
        
          return {
            id: String(contractor.id),
        
            // 👤 Name
            name: `${contractor.user_first_name || ""} ${contractor.user_last_name || ""}`.trim(),
        
            // 🏢 Company
            companyName: contractor.company_name || "",
        
            // 🖼️ Images
            logo: contractor.ua_profile_pic || "",
            cover: contractor.ua_cover_pic || "",
        
            // 🧠 Profile / Category
            profile: parsedProfile,
        
            // 🛠️ Skills
            services: parsedSkills,
        
            // 📍 Location (better formatting)
            location: [
              contractor.ua_city,
              contractor.ua_state,
              contractor.ua_country,
            ]
              .filter(Boolean)
              .join(", ") || "Unknown",
        
            // 📫 Address
            address: contractor.ua_address || "",
        
            // ⭐ Stats
            rating: Number(contractor.rating || 0),
            completedJobs: Number(contractor.ua_completed || 0),
            totalProjects: Number(contractor.total_projects || 0),
        
            // ⏳ Experience
            yearsExperience: Number(contractor.ua_exp || 0),
        
            // ⏱️ Work Info
            workHours: contractor.ua_work_hours || "",
            availability: parsedAvailability,
        
            // 💰 Pricing
            priceRange: contractor.price_range || "",
        
            // 📝 Description (FIXED 🚨)
            description:
              contractor.ua_description ||
              parsedProfile ||
              "No profile description available.",
        
            // 📞 Contact
            email: contractor.user_email || "",
            phone: contractor.user_phone || "",
        
            // 📱 Socials
            social: {
              instagram: contractor.ua_instagram_url || "",
              facebook: contractor.ua_fb_url || "",
              website: contractor.ua_social_url || "",
            },
        
            // 📅 Dates
            joiningDate: contractor.ua_joining_date || "",
            createdAt: contractor.created_at || "",
        
            // 🔒 Status
            isActive: Boolean(contractor.is_active),
            isVerified: Boolean(contractor.is_verified),
        
            // 📂 Future use
            pastJobs: [],
            portfolioImages: [],
          };
        });

        setContractors(mappedContractors);
      })
      .catch((error: any) => {
        console.log(error);
        toast.error("Unable to load contractors");
      })
      .finally(() => {
        setLoadingContractors(false);
      });
  }, []);
  useEffect(() => {
    if (!agentId) {
      return;
    }

    setLoadingHomeowners(true);
    CallAPIPostPromise(ACCESSIBLE_HOMEOWNERS_PATH,{
      agentId:getDecodeId(),status:"signed_up"
    })
    .then((res: any) => {
      const homeownerRows = res?.data || [];
    
      const mappedHomeowners = homeownerRows.map((h: any) => {
        return {
	          id: h.homeownerId,
            accessType: h.accessType || "primary",
    
          // 👤 Name
          name: h.homeowner.name,
    
          // 📍 Location
          city:h.homeowner.city,
          state:h.homeowner.state,
          location: [h.homeowner.city, h.homeowner.state].filter(Boolean).join(", ") || "Unknown",
    
          // 🏠 Address
          address: h.homeowner.address|| "",
    
          // 🛠️ Service
          serviceRequired: h.accessType === "shared" ? "Shared homeowner" : "Primary homeowner",
    
          // 📞 Contact
          phone: h.homeowner.phone || "-",
          email:h.homeowner.email || "-",
    
          // 👥 Reference
    
          // 🧾 Extra useful fields (future ready 😏)
          isActive: h.isActive,
          isVerified: true,
    
          // 📅 Dates
          createdAt: h.joinedDate || "",
    
          // 🆔 Unique
        };
      });
    
      setHomeowners(mappedHomeowners);
    })
      .catch((error: any) => {
        console.log(error);
        toast.error("Unable to load referral homeowners");
      })
      .finally(() => {
        setLoadingHomeowners(false);
      });
  }, [agentId]);

  const handleContractorsNext = (contractorIds: string[]) => {
    setSelectedContractorIds(contractorIds);
    setCurrentStep("homeowners");
  };

  const handleHomeownersNext = (homeownerIds: string[]) => {
    setSelectedHomeownerIds(homeownerIds);
    setCurrentStep("confirmation");
  };

  const selectedContractors = useMemo(
    () => contractors.filter((contractor) => selectedContractorIds.includes(contractor.id)),
    [contractors, selectedContractorIds]
  );

  const selectedHomeowners = useMemo(
    () => homeowners.filter((homeowner) => selectedHomeownerIds.includes(homeowner.id)),
    [homeowners, selectedHomeownerIds]
  );

  const handleConfirm = async () => {
    if (!agentId) {
      toast.error("Agent session not found");
      return;
    }

    setSubmitting(true);
    try {
      const responses = await Promise.all(
        selectedHomeowners.map((homeowner) =>
          CallAPIPostPromise(CREATE_SUGGESTION_PATH, {
            agentUserId: Number(agentId),
            homeownerUserId: Number(homeowner.id),
            contractorIds: selectedContractorIds.map((id) => Number(id)),
            serviceCategory: "",
            note: `Suggested from Agent Portal for ${homeowner.name}`,
            createdBy: Number(agentId),
          })
        )
      );

      const totalCreated = responses.reduce((sum: number, response: any) => {
        return sum + Number(response?.data?.totalCreated || 0);
      }, 0);

      setCreatedCount(totalCreated);
      toast.success("Contractor suggestions sent successfully");
      setCurrentStep("success");
    } catch (error: any) {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Failed to send contractor suggestions"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedContractorIds([]);
    setSelectedHomeownerIds([]);
    setCreatedCount(0);
    setCurrentStep("contractors");
  };

  return (
    <div className="page-content">
      <Container fluid>
      <div className="contractor-explore-page tailwind-prefix">
      <div className="contractor-view-switcher mb-5" aria-label="Explore contractors view">
        <button type="button" aria-pressed={pageView === "recommend"} onClick={() => setPageView("recommend")} className={`contractor-view-switcher__button${pageView === "recommend" ? " is-active" : ""}`}>
          Recommend Contractors
        </button>
        <button type="button" aria-pressed={pageView === "history"} onClick={() => setPageView("history")} className={`contractor-view-switcher__button${pageView === "history" ? " is-active" : ""}`}>
          Recommendation History
        </button>
      </div>

      {pageView === "recommend" && currentStep === "contractors" && (
        <ContractorExplorer
          contractors={contractors}
          loading={loadingContractors}
          onNext={handleContractorsNext}
        />
      )}

      {pageView === "recommend" && currentStep === "homeowners" && (
        <HomeownerSelection
          homeowners={homeowners}
          loading={loadingHomeowners}
          onNext={handleHomeownersNext}
          onBack={() => setCurrentStep("contractors")}
        />
      )}

      {pageView === "recommend" && currentStep === "confirmation" && (
        <ConfirmationScreen
          contractors={selectedContractors}
          homeowners={selectedHomeowners}
          contractorIds={selectedContractorIds}
          homeownerIds={selectedHomeownerIds}
          submitting={submitting}
          onConfirm={handleConfirm}
          onBack={() => setCurrentStep("homeowners")}
        />
      )}

      {pageView === "recommend" && currentStep === "success" && (
        <SuccessScreen onReset={handleReset} createdCount={createdCount} />
      )}
      {pageView === "history" && <RecommendationHistory />}
      </div>
      </Container>
    </div>
  );
}
